// CompendiumDrawer — browse/search all SRD content with rich detail panels,
// plus a Party gallery showing every PC's live pixel portrait + key stats.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { CompendiumDetail, CompendiumKind, CompendiumResult, PartyMember } from "../lib/types";
import { COMPENDIUM_TABS } from "../lib/srd";
import { asText, formatCR, titleizeSlug } from "../lib/format";
import { Markdown } from "./Markdown";
import { PixelArt } from "./PixelArt";
import { Drawer, Spinner } from "./ui";

export function CompendiumDrawer({
  gameId, party = [], art = {}, onOpenSheet, onClose,
}: {
  gameId?: string;
  party?: PartyMember[];
  art?: Record<string, string>;
  onOpenSheet?: (characterId: string) => void;
  onClose: () => void;
}) {
  const showParty = !!gameId && party.length > 0;
  const [view, setView] = useState<"party" | "srd">(showParty ? "party" : "srd");
  const [kind, setKind] = useState<CompendiumKind>("spells");
  const [q, setQ] = useState("");
  const [results, setResults] = useState<CompendiumResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<CompendiumDetail | null>(null);

  useEffect(() => {
    if (view !== "srd") return;
    let alive = true;
    setLoading(true);
    const t = window.setTimeout(() => {
      api.compendium(kind, { q, limit: 80 })
        .then((r) => alive && setResults(r.results))
        .catch(() => alive && setResults([]))
        .finally(() => alive && setLoading(false));
    }, 200);
    return () => { alive = false; window.clearTimeout(t); };
  }, [kind, q, view]);

  async function open(index: string) {
    setDetail(null);
    try { setDetail(await api.compendiumDetail(kind, index)); } catch { /* ignore */ }
  }

  return (
    <Drawer title="Compendium" onClose={onClose}
      actions={detail && <button className="btn btn--sm btn--ghost" onClick={() => setDetail(null)}>← Back</button>}>
      {!detail && (
        <div className="comp-mode">
          {showParty && (
            <button className={`comp-mode__btn${view === "party" ? " on" : ""}`}
              onClick={() => setView("party")}>🎭 The Party</button>
          )}
          <button className={`comp-mode__btn${view === "srd" ? " on" : ""}`}
            onClick={() => setView("srd")}>📖 Rules & Content</button>
        </div>
      )}

      {!detail && view === "party" && showParty ? (
        <div className="party-gallery">
          {party.map((p) => (
            <button key={p.id} className="pg-card" onClick={() => onOpenSheet?.(p.id)}
              title="Open full sheet">
              <PixelArt
                shape="square"
                className="pg-portrait"
                alt={p.name}
                body={{ kind: "character", game_id: gameId, character_id: p.id }}
                liveUrl={art[`character:${p.id}`]}
              />
              <div className="pg-info">
                <div className="pg-name">{p.name}</div>
                <div className="pg-summary">{p.summary}</div>
                <div className="pg-stats">
                  <span>🛡 {p.ac}</span>
                  <span>❤ {p.hp}/{p.max_hp}</span>
                  <span>Lv {p.level}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : !detail ? (
        <>
          <div className="comp-tabs">
            {COMPENDIUM_TABS.map((t) => (
              <button key={t.kind} className={`comp-tab${kind === t.kind ? " on" : ""}`}
                onClick={() => { setKind(t.kind); setDetail(null); }}>{t.label}</button>
            ))}
          </div>
          <input className="field" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${kind.replace("-", " ")}…`} />
          <div className="comp-results mt12">
            {loading && <div className="muted flex"><Spinner /> Searching…</div>}
            {!loading && results.length === 0 && <div className="muted">No matches.</div>}
            {results.map((r) => (
              <div key={r.index} className="comp-item" onClick={() => open(r.index)}>
                <span className="ci-name">{r.name}</span>
                <span className="ci-meta">
                  {r.level != null && (r.level === 0 ? "Cantrip" : `Lvl ${r.level}`)}
                  {r.school && ` · ${r.school}`}
                  {r.challenge_rating != null && `CR ${formatCR(r.challenge_rating)}`}
                  {r.rarity && titleizeSlug(String(r.rarity))}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <CompDetail kind={kind} d={detail!} />
      )}
    </Drawer>
  );
}

function CompDetail({ kind, d }: { kind: CompendiumKind; d: CompendiumDetail }) {
  const desc: string[] = Array.isArray(d.desc) ? d.desc : d.desc ? [String(d.desc)] : [];

  return (
    <div className="comp-detail">
      <h2>{d.name}</h2>
      {kind === "spells" && (
        <div className="cd-sub">
          {d.level === 0 ? `${asText(d.school)} cantrip` : `Level ${d.level} ${asText(d.school)}`}
          {d.concentration ? " · Concentration" : ""}{d.ritual ? " · Ritual" : ""}
        </div>
      )}
      {kind === "monsters" && (
        <div className="cd-sub">{d.size} {asText(d.type)} · CR {formatCR(d.challenge_rating)} · {d.xp} XP</div>
      )}
      {(kind === "magic-items" || kind === "equipment") && d.rarity && (
        <div className="cd-sub">{asText(d.rarity)} · {asText(d.equipment_category)}</div>
      )}

      {kind === "spells" && (
        <div className="cd-grid">
          <span className="k">Casting Time</span><span className="v">{d.casting_time}</span>
          <span className="k">Range</span><span className="v">{d.range}</span>
          <span className="k">Components</span><span className="v">{(d.components || []).join(", ")}</span>
          <span className="k">Duration</span><span className="v">{d.duration}</span>
        </div>
      )}

      {kind === "monsters" && (
        <>
          <div className="cd-grid">
            <span className="k">Armor Class</span><span className="v">{acText(d.armor_class)}</span>
            <span className="k">Hit Points</span><span className="v">{d.hit_points} ({d.hit_dice})</span>
            <span className="k">Speed</span><span className="v">{speedText(d.speed)}</span>
            <span className="k">Senses</span><span className="v">{sensesText(d.senses)}</span>
          </div>
          <div className="cd-grid cd-statblock">
            {(["strength","dexterity","constitution","intelligence","wisdom","charisma"] as const).map((s) => (
              <span key={s} className="v" style={{ textAlign: "center" }}>
                {s.slice(0,3).toUpperCase()} {d[s]} ({mod(d[s])})
              </span>
            ))}
          </div>
        </>
      )}

      <div className="cd-desc mt16">
        <Markdown text={desc.join("\n\n")} />
      </div>

      {kind === "monsters" && d.special_abilities?.length > 0 && (
        <div className="mt16">
          <div className="label">Traits</div>
          {d.special_abilities.map((a: any, i: number) => (
            <p key={i} style={{ fontSize: 13.5, marginBottom: 8 }}>
              <b style={{ color: "var(--gold)" }}>{a.name}.</b> {a.desc}
            </p>
          ))}
        </div>
      )}
      {kind === "monsters" && d.actions?.length > 0 && (
        <div className="mt16">
          <div className="label">Actions</div>
          {d.actions.map((a: any, i: number) => (
            <p key={i} style={{ fontSize: 13.5, marginBottom: 8 }}>
              <b style={{ color: "var(--ember-bright)" }}>{a.name}.</b> {a.desc}
            </p>
          ))}
        </div>
      )}

      {d.higher_level?.length > 0 && (
        <div className="mt16">
          <div className="label">At Higher Levels</div>
          {d.higher_level.map((p: string, i: number) => <p key={i} style={{ fontSize: 13.5 }}>{p}</p>)}
        </div>
      )}
    </div>
  );
}

const mod = (s: number) => { const m = Math.floor((s - 10) / 2); return m >= 0 ? `+${m}` : `${m}`; };
function acText(ac: any): string {
  if (Array.isArray(ac)) return ac.map((a) => a.value).join(", ");
  return String(ac);
}
function speedText(sp: any): string {
  if (!sp || typeof sp !== "object") return String(sp || "—");
  return Object.entries(sp).map(([k, v]) => `${k} ${v}`).join(", ");
}
function sensesText(s: any): string {
  if (!s || typeof s !== "object") return "—";
  return Object.entries(s).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(", ");
}
